import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { setBooleanAttr } from '../../utils/ids-attribute-utils/ids-attribute-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsElement from '../../core/ids-element';

import styles from './ids-separator.scss';

const Base = IdsColorVariantMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/** Map of spacing token shortcuts to CSS variables */
const SPACING_MAP: Record<string, string> = {
  '2xs': 'var(--ids-space-2xs)',
  xs: 'var(--ids-space-xs)',
  sm: 'var(--ids-space-sm)',
  md: 'var(--ids-space-md)',
  lg: 'var(--ids-space-lg)',
  xl: 'var(--ids-space-xl)',
  '2xl': 'var(--ids-space-2xl)'
};

/**
 * IDS Separator Component
 * @type {IdsSeparator}
 * @inherits IdsElement
 * @mixes IdsColorVariantMixin
 * @mixes IdsEventsMixin
 * @part separator - the menu separator element
 */
@customElement('ids-separator')
@scss(styles)
export default class IdsSeparator extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.vertical = this.vertical;
  }

  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.APPEARANCE,
      attributes.VERTICAL,
      attributes.NO_MARGINS,
      attributes.MARGIN_BLOCK_START,
      attributes.MARGIN_BLOCK_END,
      attributes.MARGIN_INLINE_START,
      attributes.MARGIN_INLINE_END
    ];
  }

  /**
   * Inherited from `IdsColorVariantMixin`
   * @returns {Array<string>} List of available color variants for this component
   */
  colorVariants: Array<string> = ['alternate-formatter', 'module-nav'];

  /**
   * @returns {string} The components template
   */
  template(): string {
    let tagName = 'div';
    if (this.parentElement?.tagName === 'IDS-MENU-GROUP') {
      tagName = 'li';
    }
    const appearanceClass = this.appearance ? ` appearance-${this.appearance}` : '';
    return `<${tagName} part="separator" class="ids-separator${this.vertical ? ' vertical' : ''}${this.noMargins ? ' no-margins' : ''}${appearanceClass}"></${tagName}>`;
  }

  /**
   * Set the appearance style of the separator
   * @param {string | null} val the appearance value (e.g., 'form')
   */
  set appearance(val: string | null) {
    const currentAppearance = this.getAttribute(attributes.APPEARANCE);
    if (currentAppearance) {
      this.container?.classList.remove(`appearance-${currentAppearance}`);
    }
    if (val) {
      this.setAttribute(attributes.APPEARANCE, val);
      this.container?.classList.add(`appearance-${val}`);
    } else {
      this.removeAttribute(attributes.APPEARANCE);
    }
  }

  get appearance(): string | null {
    return this.getAttribute(attributes.APPEARANCE);
  }

  /**
   * Set the separator to be vertical
   */
  set vertical(val: boolean) {
    setBooleanAttr(attributes.VERTICAL, this, val);
  }

  get vertical(): boolean {
    return stringToBool(this.getAttribute(attributes.VERTICAL));
  }

  /**
   * Enforce no margins on the separator if defined
   */
  set noMargins(val: boolean | string) {
    setBooleanAttr(attributes.NO_MARGINS, this, val);
  }

  /**
   * Get the no margins state
   * @returns {boolean | string} true if margins are disabled
   */
  get noMargins(): boolean | string {
    return this.hasAttribute(attributes.NO_MARGINS);
  }

  /**
   * Set margin-block-start (top margin for horizontal separator)
   * @param {string | null} val the margin value
   */
  set marginBlockStart(val: string | null) {
    if (val) {
      this.setAttribute('margin-block-start', val);
    } else {
      this.removeAttribute('margin-block-start');
    }
    this.#updateMargins();
  }

  get marginBlockStart(): string | null {
    return this.getAttribute('margin-block-start');
  }

  /**
   * Set margin-block-end (bottom margin for horizontal separator)
   * @param {string | null} val the margin value
   */
  set marginBlockEnd(val: string | null) {
    if (val) {
      this.setAttribute('margin-block-end', val);
    } else {
      this.removeAttribute('margin-block-end');
    }
    this.#updateMargins();
  }

  get marginBlockEnd(): string | null {
    return this.getAttribute('margin-block-end');
  }

  /**
   * Set margin-inline-start (left margin for vertical separator)
   * @param {string | null} val the margin value
   */
  set marginInlineStart(val: string | null) {
    if (val) {
      this.setAttribute('margin-inline-start', val);
    } else {
      this.removeAttribute('margin-inline-start');
    }
    this.#updateMargins();
  }

  get marginInlineStart(): string | null {
    return this.getAttribute('margin-inline-start');
  }

  /**
   * Set margin-inline-end (right margin for vertical separator)
   * @param {string | null} val the margin value
   */
  set marginInlineEnd(val: string | null) {
    if (val) {
      this.setAttribute('margin-inline-end', val);
    } else {
      this.removeAttribute('margin-inline-end');
    }
    this.#updateMargins();
  }

  get marginInlineEnd(): string | null {
    return this.getAttribute('margin-inline-end');
  }

  /**
   * Convert a margin value to CSS (handles tokens like 'lg' or raw values like '16px')
   * @param {string | null} val the margin value
   * @returns {string | null} the CSS value
   */
  #toMarginCss(val: string | null): string | null {
    if (!val) return null;
    return SPACING_MAP[val] || val;
  }

  /**
   * Update the margin CSS variables on the host
   */
  #updateMargins() {
    const marginBlockStart = this.#toMarginCss(this.marginBlockStart);
    const marginBlockEnd = this.#toMarginCss(this.marginBlockEnd);
    const marginInlineStart = this.#toMarginCss(this.marginInlineStart);
    const marginInlineEnd = this.#toMarginCss(this.marginInlineEnd);

    if (marginBlockStart) {
      this.style.setProperty('--ids-separator-margin-block-start', marginBlockStart);
    } else {
      this.style.removeProperty('--ids-separator-margin-block-start');
    }

    if (marginBlockEnd) {
      this.style.setProperty('--ids-separator-margin-block-end', marginBlockEnd);
    } else {
      this.style.removeProperty('--ids-separator-margin-block-end');
    }

    if (marginInlineStart) {
      this.style.setProperty('--ids-separator-margin-inline-start', marginInlineStart);
    } else {
      this.style.removeProperty('--ids-separator-margin-inline-start');
    }

    if (marginInlineEnd) {
      this.style.setProperty('--ids-separator-margin-inline-end', marginInlineEnd);
    } else {
      this.style.removeProperty('--ids-separator-margin-inline-end');
    }
  }

  /**
   * Set the hidden state of the separator
   * @param {boolean} value whether the separator should be hidden
   */
  set hidden(value: boolean) {
    this.toggleAttribute(attributes.HIDDEN, value);
  }

  /**
   * @returns {boolean} true if this separator is hidden
   */
  get hidden(): boolean {
    return this.hasAttribute(attributes.HIDDEN);
  }
}
